import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { PageChangeEvent } from '../../../shared/models/design-system.types';

/**
 * Pagination Component
 *
 * A flexible pagination component for tables and lists.
 *
 * Usage:
 * ```html
 * <hc-pagination
 *   [page]="currentPage"
 *   [perPage]="pageSize"
 *   [total]="totalItems"
 *   [pageSizeOptions]="[10, 25, 50]"
 *   (pageChange)="onPageChange($event)"
 * ></hc-pagination>
 * ```
 */
@Component({
    selector: 'hc-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    standalone: false
})
export class PaginationComponent implements OnChanges {
  /** Current page (1-indexed) */
  @Input() page = 1;

  /** Items per page */
  @Input() perPage = 10;

  /** Total number of items */
  @Input() total = 0;

  /** Available page size options */
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];

  /** Whether to show page size selector */
  @Input() showPageSizeSelector = true;

  /** Whether to show first/last buttons */
  @Input() showFirstLastButtons = true;

  /** Maximum number of page buttons to show */
  @Input() maxVisiblePages = 5;

  /** Whether to show item count summary */
  @Input() showSummary = true;

  /** Emits when page or page size changes */
  @Output() pageChange = new EventEmitter<PageChangeEvent>();

  /** Computed page numbers to display */
  pageNumbers: (number | '...')[] = [];

  /** Total number of pages */
  get totalPages(): number {
    return Math.ceil(this.total / this.perPage) || 1;
  }

  /** Whether we're on the first page */
  get isFirstPage(): boolean {
    return this.page === 1;
  }

  /** Whether we're on the last page */
  get isLastPage(): boolean {
    return this.page === this.totalPages;
  }

  /** Starting item number for current page */
  get startItem(): number {
    if (this.total === 0) return 0;
    return (this.page - 1) * this.perPage + 1;
  }

  /** Ending item number for current page */
  get endItem(): number {
    return Math.min(this.page * this.perPage, this.total);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['page'] || changes['total'] || changes['perPage'] || changes['maxVisiblePages']) {
      this.calculatePageNumbers();
    }
  }

  /**
   * Calculate which page numbers to display
   */
  private calculatePageNumbers(): void {
    const pages: (number | '...')[] = [];
    const total = this.totalPages;
    const current = this.page;
    const max = this.maxVisiblePages;

    if (total <= max) {
      // Show all pages
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of visible range
      let start = Math.max(2, current - Math.floor((max - 3) / 2));
      const end = Math.min(total - 1, start + max - 3);

      // Adjust if we're near the end
      if (end === total - 1) {
        start = Math.max(2, end - (max - 3));
      }

      // Add ellipsis before range if needed
      if (start > 2) {
        pages.push('...');
      }

      // Add range
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis after range if needed
      if (end < total - 1) {
        pages.push('...');
      }

      // Always show last page
      pages.push(total);
    }

    this.pageNumbers = pages;
  }

  /**
   * Navigate to a specific page
   */
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) {
      return;
    }
    this.page = page;
    this.emitChange();
  }

  /**
   * Go to the previous page
   */
  previousPage(): void {
    this.goToPage(this.page - 1);
  }

  /**
   * Go to the next page
   */
  nextPage(): void {
    this.goToPage(this.page + 1);
  }

  /**
   * Go to the first page
   */
  firstPage(): void {
    this.goToPage(1);
  }

  /**
   * Go to the last page
   */
  lastPage(): void {
    this.goToPage(this.totalPages);
  }

  /**
   * Handle page size change from ng-select.
   * ng-select emits the selected value directly (not a DOM event).
   */
  onPageSizeChange(value: number): void {
    this.perPage = value;
    this.page = 1; // Reset to first page when changing page size
    this.emitChange();
  }

  /**
   * Emit page change event
   */
  private emitChange(): void {
    this.calculatePageNumbers();
    this.pageChange.emit({
      page: this.page,
      perPage: this.perPage
    });
  }

  /**
   * Track function for ngFor
   */
  trackByPage(index: number, item: number | '...'): number | string {
    return typeof item === 'number' ? item : `ellipsis-${index}`;
  }
}
